import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';
import { CONFIG } from '../utils/config';
import { useContent } from '../hooks/useContent';

const Shop = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { addToCart, itemCount, isAuthenticated } = useCart();
  const { content, loading: contentLoading } = useContent('shop');
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 12;
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTags, setActiveSearchTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchInputRef = React.useRef(null);

  useEffect(() => {
    const fetchShopQRCodes = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({
          miniature: 'true',
          miniature_quality: '80',
          miniature_size: '256',
          page: currentPage.toString(),
          limit: itemsPerPage.toString()
        });

        if (activeSearchTags.length > 0) {
          params.append('tags', activeSearchTags.join(','));
          params.append('tag_matching', 'any');
        }

        const response = await fetch(`${CONFIG.BASE_URL}/qr-code-shop?${params.toString()}`);

        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const qrCodesList = Array.isArray(data?.qr_codes) ? data.qr_codes : [];
        const lastPage = Number(data?.last_page) || 1;

        setQrCodes(qrCodesList);
        setTotalPages(Math.max(lastPage, 1));

        if (currentPage > lastPage) {
          setCurrentPage(Math.max(lastPage, 1));
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des QR codes:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchShopQRCodes();
  }, [currentPage, itemsPerPage, activeSearchTags]);

  useEffect(() => {
    const fetchAvailableTags = async () => {
      try {
        const response = await fetch(`${CONFIG.BASE_URL}/qr-code-tags`);
        console.log('Réponse des tags:', response);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data?.tags)) {
            const sortedTags = data.tags
              .sort((a, b) => b.count - a.count)
              .map(tag => tag.label);
              console.log('Tags disponibles:', sortedTags);
            setAvailableTags(sortedTags);
          }
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des tags:', err);
      }
    };

    fetchAvailableTags();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearchTags]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleAddToCart = async (qrCode) => {
    if (!isAuthenticated()) {
      navigate('/login?redirect=/shop');
      return;
    }

    setAddingToCart(qrCode.uuid);

    const result = await addToCart(qrCode.uuid);

    if (result.success) {
      setQrCodes(prev => {
        const nextItems = prev.filter(item => item.uuid !== qrCode.uuid);
        if (nextItems.length === 0 && currentPage > 1) {
          setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
        }
        return nextItems;
      });
      setNotification({ type: 'success', message: content.cart.added });
    } else {
      setNotification({ type: 'error', message: result.message });
    }

    setAddingToCart(null);
  };

  const filteredSuggestions = React.useMemo(() => {
    if (!searchTerm.trim()) return [];
    const searchLower = searchTerm.toLowerCase();
    return availableTags.filter(tag =>
      tag.toLowerCase().includes(searchLower) && !activeSearchTags.includes(tag)
    ).slice(0, 8);
  }, [searchTerm, availableTags, activeSearchTags]);

  const handleSelectTag = (tag) => {
    if (!activeSearchTags.includes(tag)) {
      setActiveSearchTags(prev => [...prev, tag]);
    }
    setSearchTerm('');
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const handleRemoveTag = (tagToRemove) => {
    setActiveSearchTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredSuggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        const trimmedTag = searchTerm.trim();
        if (trimmedTag && !activeSearchTags.includes(trimmedTag)) {
          setActiveSearchTags(prev => [...prev, trimmedTag]);
          setSearchTerm('');
        }
        setShowSuggestions(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSelectTag(filteredSuggestions[selectedSuggestionIndex]);
        } else {
          const trimmedTag = searchTerm.trim();
          if (trimmedTag && !activeSearchTags.includes(trimmedTag)) {
            setActiveSearchTags(prev => [...prev, trimmedTag]);
            setSearchTerm('');
          }
          setShowSuggestions(false);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveSearchTags([]);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const filteredQRCodes = qrCodes;

  if (contentLoading || !content) {
    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className={`pt-16 min-h-screen ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-primary-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-secondary-500 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-full border border-primary-500/30 mb-6">
            <svg className="w-5 h-5 text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <span className="text-primary-400 font-medium">{content.hero.badge}</span>
          </div>

          <h1 className={`text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {content.hero.title.line1} <span className="bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">{content.hero.title.line2}</span>
          </h1>

          <p className={`text-lg md:text-xl max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {content.hero.description}
          </p>

          {itemCount > 0 && (
            <Link
              to="/cart"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 border border-primary-500/30 rounded-xl hover:from-primary-500/30 hover:to-secondary-500/30 transition-all duration-200"
            >
              <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="text-primary-400 font-medium">{content.cart.viewCart} ({itemCount})</span>
            </Link>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className={`rounded-xl border p-6 mb-6 ${
          isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <div className="flex flex-col gap-4">
            <div className="relative w-full">
              <svg
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={content.search.placeholder}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSuggestions(true);
                  setSelectedSuggestionIndex(-1);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                onKeyDown={handleKeyDown}
                className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  isDark
                    ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-dark-600 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {showSuggestions && filteredSuggestions.length > 0 && (
                <ul className={`absolute z-50 w-full mt-1 rounded-lg border shadow-lg max-h-60 overflow-auto ${
                  isDark ? 'bg-dark-700 border-dark-600' : 'bg-white border-gray-200'
                }`}>
                  {filteredSuggestions.map((tag, index) => (
                    <li
                      key={tag}
                      onClick={() => handleSelectTag(tag)}
                      className={`px-4 py-3 cursor-pointer flex items-center gap-2 ${
                        index === selectedSuggestionIndex
                          ? isDark ? 'bg-primary-600/30' : 'bg-primary-50'
                          : isDark ? 'hover:bg-dark-600' : 'hover:bg-gray-50'
                      } ${isDark ? 'text-white' : 'text-gray-900'}`}
                    >
                      <svg
                        className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span>{tag}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {activeSearchTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {content.search.activeLabel}
                </span>
                {activeSearchTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-sm font-medium rounded-full"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
                {activeSearchTags.length > 1 && (
                  <button
                    onClick={handleClearSearch}
                    className={`text-sm underline ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'}`}
                  >
                    {content.search.clearAll}
                  </button>
                )}
              </div>
            )}

            {activeSearchTags.length > 0 && (
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {filteredQRCodes.length} {filteredQRCodes.length !== 1 ? content.search.resultPlural : content.search.resultSingular} {filteredQRCodes.length !== 1 ? content.search.foundPlural : content.search.foundSingular}
              </div>
            )}
          </div>
        </div>
      </section>

      {notification && (
        <div className={`fixed top-20 right-4 z-50 p-4 rounded-xl shadow-lg transition-all duration-300 ${
          notification.type === 'success' 
            ? isDark ? 'bg-green-900/90 border border-green-500/30 text-green-300' : 'bg-green-50 border border-green-200 text-green-700'
            : isDark ? 'bg-red-900/90 border border-red-500/30 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20">
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        )}

        {error && (
          <div className={`text-center py-16 ${isDark ? 'text-red-400' : 'text-red-500'}`}>
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg">{content.loadingError} {error}</p>
          </div>
        )}

        {!loading && !error && filteredQRCodes.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredQRCodes.map((qrCode) => (
              <div
                key={qrCode.uuid}
                className={`relative rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                  isDark 
                    ? 'bg-dark-800 border border-dark-700' 
                    : 'bg-white border border-gray-200 shadow-md'
                }`}
              >
                <div className={`aspect-square p-4 flex items-center justify-center ${
                  isDark ? 'bg-dark-900' : 'bg-gray-50'
                }`}>
                  {qrCode.image ? (
                    <img 
                      src={qrCode.image}
                      alt={`QR Code ${qrCode.uuid}`}
                      className="w-full h-full object-contain rounded-xl"
                    />
                  ) : qrCode.miniature ? (
                    <img 
                      src={`data:image/webp;base64,${qrCode.miniature}`}
                      alt={`QR Code ${qrCode.uuid}`}
                      className="w-full h-full object-contain rounded-xl"
                    />
                  ) : (
                    <div className={`w-full h-full rounded-xl flex items-center justify-center ${
                      isDark ? 'bg-dark-700' : 'bg-gray-100'
                    }`}>
                      <svg className={`w-32 h-32 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h3v2h-3v-2zm-5 0h2v2h-2v-2zm2 2h2v2h-2v-2zm-2 2h2v2h-2v-2zm2 2h2v2h-2v-2zm2-2h3v4h-3v-4z"/>
                      </svg>
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {content.product.price}{content.product.currency}
                      </span>
                      <span className={`text-xs ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        TTC
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleAddToCart(qrCode)}
                    disabled={addingToCart === qrCode.uuid}
                    className="w-full py-3 px-4 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingToCart === qrCode.uuid ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{content.cart.adding}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>{content.cart.addButton}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && totalPages > 1 && (
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || loading}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-200 ${
                isDark
                  ? 'border-dark-700 bg-dark-800 text-gray-300 hover:bg-dark-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              } ${currentPage === 1 || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {content.pagination.previous}
            </button>

            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {content.pagination.page} {currentPage} {content.pagination.of} {totalPages}
            </span>

            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || loading}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-200 ${
                isDark
                  ? 'border-dark-700 bg-dark-800 text-gray-300 hover:bg-dark-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              } ${currentPage === totalPages || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {content.pagination.next}
            </button>
          </div>
        )}

        {!loading && !error && qrCodes.length === 0 && activeSearchTags.length === 0 && (
          <div className={`text-center py-16 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg">{content.empty}</p>
          </div>
        )}

        {!loading && !error && qrCodes.length === 0 && activeSearchTags.length > 0 && (
          <div className={`text-center py-16 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-lg mb-4">{content.noResults} "{activeSearchTags.join(', ')}".</p>
            <button
              onClick={handleClearSearch}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isDark
                  ? 'bg-primary-600 hover:bg-primary-700 text-white'
                  : 'bg-primary-500 hover:bg-primary-600 text-white'
              }`}
            >
              {content.resetSearch}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default Shop;
